import Link from "next/link";
import type { ComponentType, ReactNode } from "react";

import { workspaceLink } from "@/components/content/workspace-link";
import { cn } from "@/lib/utils";
import {
  MENTION_ENTITY_STYLES,
  type MentionEntityType,
} from "./entity-mention-input";

export type ContextChipType = Exclude<MentionEntityType, "teammate">;

/** The channel, Data Set or view a chip's link names, for its card. */
export type ContextChipTarget =
  | { type: "channel"; id: string; label: string }
  | { type: "dataSet"; id: string; channelId: string; label: string }
  | { type: "view"; id: string; channelId: string; label: string };

/** What opens a named channel, Data Set or view's card from its chip. */
export type ContextChipHoverComponent = ComponentType<{
  mention: ContextChipTarget;
  href: string | null;
  className: string;
  children: ReactNode;
}>;

/**
 * Where something happened, in the mention-chip vocabulary — channels violet,
 * Data Sets amber, views teal — so context reads identically wherever it is
 * named: feed sentences, thread sheets, record cards, inbox headers. A chip
 * whose link names a channel, Data Set or view opens that thing's card; without
 * `hover` it renders closed, with the attributes the card gives a closed chip.
 */
export function ContextChipView({
  type,
  label,
  href,
  className,
  hover: Hover,
}: {
  type: ContextChipType;
  label: string;
  href?: string | null;
  className?: string;
  hover?: ContextChipHoverComponent;
}) {
  const text = type === "channel" ? `#${label}` : label;
  const chipClass = cn(
    // A chip moves to the next line whole rather than breaking at a hyphen ("CRM follow-" / "ups").
    "whitespace-nowrap rounded px-0.5 font-medium",
    MENTION_ENTITY_STYLES[type].inline,
    className,
  );
  if (!href) return <span className={chipClass}>{text}</span>;
  const target = contextTarget(type, label, href);
  const linkClass = cn(chipClass, "underline-offset-2 hover:underline");
  if (target)
    return Hover ? (
      <Hover mention={target} href={href} className={linkClass}>
        {text}
      </Hover>
    ) : (
      <Link
        href={href}
        prefetch={false}
        className={linkClass}
        aria-haspopup="dialog"
        aria-expanded={false}
      >
        {text}
      </Link>
    );
  return (
    <Link href={href} className={linkClass}>
      {text}
    </Link>
  );
}

function contextTarget(type: ContextChipType, label: string, href: string): ContextChipTarget | null {
  const link = workspaceLink(href, "");
  if (!link) return null;
  if (type === "channel" && link.kind === "channel") return { type: "channel", id: link.channelId, label };
  if (type === "dataSet" && link.kind === "dataSet") return { type: "dataSet", id: link.dataSetId, channelId: link.channelId, label };
  if (type === "view" && link.kind === "view") return { type: "view", id: link.view, channelId: link.channelId, label };
  return null;
}

export function contextChannelHref(channelId?: string | null): string | null {
  return channelId
    ? `/workspace/channels/${encodeURIComponent(channelId)}`
    : null;
}

export function contextDataSetHref(
  channelId?: string | null,
  dataSetId?: string | null,
): string | null {
  if (!channelId || !dataSetId) return null;
  return `/workspace/channels/${encodeURIComponent(channelId)}?dataSet=${encodeURIComponent(dataSetId)}`;
}
